import { useDeployCollection } from "@/hooks/useDeployCollection";
import { useChainMismatch } from "@/hooks/useChainMismatch";

/**
 * Wizard step 3 — confirm summary and fire the deploy. The deploy logic
 * (factory.create*, register, setSigner for badges, PFP associate) lives
 * in useDeployCollection so this stays a thin renderer.
 */
export function Step3Deploy() {
  const { deploy, status, isPending, factoryConfigured, chain, wizard: w } =
    useDeployCollection();
  // Deploy targets the active chain — block if the wallet's somewhere else.
  const guard = useChainMismatch(chain?.chainId ?? 0);

  const summary =
    w.kind === "badge" ? (
      <div className="card space-y-2 text-sm">
        <div><span className="text-muted-foreground">Type:</span> Badge</div>
        <div>
          <span className="text-muted-foreground">Name / Symbol:</span> {w.name || "—"} ({w.symbol || "—"})
        </div>
        <div><span className="text-muted-foreground">Chain:</span> {chain?.name}</div>
        <div>
          <span className="text-muted-foreground">Max supply:</span>{" "}
          {w.maxSupply === 0 ? "unlimited" : w.maxSupply}
        </div>
        <div>
          <span className="text-muted-foreground">Valid until:</span>{" "}
          {w.badgeValidUntil || "no expiration"}
        </div>
        <div><span className="text-muted-foreground">Soulbound:</span> {w.badgeSoulbound ? "yes" : "no"}</div>
        <div>
          <span className="text-muted-foreground">One per address:</span> {w.badgeOneShot ? "yes" : "no"}
        </div>
        <div>
          <span className="text-muted-foreground">Gas:</span>{" "}
          {w.badgeSubsidized ? "subsidized by you (relayer wallet)" : "claimer pays"}
        </div>
      </div>
    ) : (
      <div className="card space-y-2 text-sm">
        <div><span className="text-muted-foreground">Name:</span> {w.name || "(unnamed)"}</div>
        <div><span className="text-muted-foreground">Symbol:</span> {w.symbol || "—"}</div>
        <div><span className="text-muted-foreground">Chain:</span> {chain?.name}</div>
        <div>
          <span className="text-muted-foreground">Supply:</span>{" "}
          {w.openEdition ? "open edition" : w.maxSupply}
        </div>
        <div>
          <span className="text-muted-foreground">Price:</span> {w.mintPriceEth}{" "}
          {chain?.nativeCurrency.symbol}
        </div>
        <div>
          <span className="text-muted-foreground">Window:</span> {w.mintStart || "now"} →{" "}
          {w.mintEnd || "∞"}
        </div>
        <div>
          <span className="text-muted-foreground">Royalty:</span> {w.royaltyBps / 100}% to{" "}
          {w.royaltyReceiver || "(your wallet)"}
        </div>
      </div>
    );

  return (
    <div className="space-y-4">
      {summary}
      {!factoryConfigured && (
        <div className="card border-amber-300 bg-amber-50 text-sm text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-300">
          No factory deployed on <code>{chain?.name}</code>.
        </div>
      )}
      {guard.wrong && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-200">
          <span>Wallet is on a different chain.</span>
          <button
            className="rounded border border-amber-500 px-2 py-1 text-xs font-semibold hover:bg-amber-500 hover:text-amber-50 disabled:opacity-50"
            onClick={guard.switchToTarget}
            disabled={guard.isSwitching}
          >
            {guard.isSwitching ? "Switching…" : `Switch to ${guard.targetName}`}
          </button>
        </div>
      )}
      <button
        className="btn-primary w-full"
        disabled={!factoryConfigured || isPending || guard.wrong}
        title={guard.tooltip}
        onClick={() => void deploy()}
      >
        {isPending ? "Deploying…" : `Deploy ${w.kind === "badge" ? "badge" : "collection"}`}
      </button>
      {status && <div className="text-sm text-muted-foreground">{status}</div>}
    </div>
  );
}
